import { useState, useEffect, useRef, useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const ROLES = [
  { value: 'org_admin', label: 'Org Admin', scopeType: 'organization' },
  { value: 'campus_pastor', label: 'Campus Pastor', scopeType: 'campus' },
  { value: 'ministry_director', label: 'Ministry Director', scopeType: 'ministry' },
  { value: 'group_leader', label: 'Group Leader', scopeType: 'group' },
  { value: 'group_member', label: 'Group Member', scopeType: 'group' },
];

export default function GrantRoleModal({ isOpen, onClose, onConfirm, orgTree = [], userId }) {
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedScopeId, setSelectedScopeId] = useState('');
  const [loading, setLoading] = useState(false);
  const backdropRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setSelectedOrgId('');
      setSelectedRole('');
      setSelectedScopeId('');
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedRole('');
    setSelectedScopeId('');
  }, [selectedOrgId]);

  useEffect(() => {
    setSelectedScopeId('');
  }, [selectedRole]);

  const roleDef = ROLES.find((r) => r.value === selectedRole);
  const scopeType = roleDef?.scopeType || '';

  const selectedOrg = orgTree.find((o) => o.id === selectedOrgId);

  // Build scope options based on role type and selected org (must be before early return for Rules of Hooks)
  const scopeOptions = useMemo(() => {
    if (!selectedOrg || !scopeType) return [];

    if (scopeType === 'organization') {
      // Scope is the org itself
      return [{ id: selectedOrg.id, name: selectedOrg.name }];
    }

    if (scopeType === 'campus') {
      return (selectedOrg.campuses || []).map((c) => ({ id: c.id, name: c.name }));
    }

    if (scopeType === 'ministry') {
      const ministries = [];
      for (const campus of selectedOrg.campuses || []) {
        for (const ministry of campus.ministries || []) {
          ministries.push({ id: ministry.id, name: `${ministry.name} (${campus.name})` });
        }
      }
      return ministries;
    }

    if (scopeType === 'group') {
      const groups = [];
      for (const campus of selectedOrg.campuses || []) {
        for (const group of campus.groups || []) {
          groups.push({ id: group.id, name: `${group.name} (${campus.name})` });
        }
        for (const ministry of campus.ministries || []) {
          for (const group of ministry.groups || []) {
            groups.push({ id: group.id, name: `${group.name} (${ministry.name})` });
          }
        }
      }
      // Groups directly under org
      for (const group of selectedOrg.groups || []) {
        groups.push({ id: group.id, name: group.name });
      }
      return groups;
    }

    return [];
  }, [selectedOrg, scopeType]);

  // Auto-select scope for org_admin
  useEffect(() => {
    if (scopeType === 'organization' && scopeOptions.length === 1) {
      setSelectedScopeId(scopeOptions[0].id);
    }
  }, [scopeType, scopeOptions]);

  if (!isOpen) return null;

  const canGrant = selectedOrgId && selectedRole && selectedScopeId;

  const handleGrant = async () => {
    if (!canGrant) return;
    setLoading(true);
    try {
      await onConfirm({
        role: selectedRole,
        scopeType: scopeType,
        scopeId: selectedScopeId,
        organizationId: selectedOrgId,
      });
      onClose();
    } catch (err) {
      // Error handled by parent
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}
    >
      <div className="absolute inset-0 bg-deep-navy/60" />

      <div className="relative bg-white rounded-card shadow-card p-6 max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-deep-navy">Grant Role</h3>
          <button onClick={onClose} className="min-h-touch min-w-touch flex items-center justify-center rounded-lg hover:bg-gray-100 transition-all">
            <XMarkIcon className="w-5 h-5 text-storm-blue" />
          </button>
        </div>

        {/* Form */}
        <div className="space-y-4">
          {/* Organization */}
          <div>
            <label className="block text-sm font-medium text-storm-blue mb-1">Organization *</label>
            <select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="w-full px-3 py-2.5 min-h-touch rounded-xl border-2 border-gray-200 focus:border-storm-blue focus:outline-none transition-all text-[16px] bg-white"
            >
              <option value="">Select organization...</option>
              {orgTree.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-storm-blue mb-1">Role *</label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              disabled={!selectedOrgId}
              className="w-full px-3 py-2.5 min-h-touch rounded-xl border-2 border-gray-200 focus:border-storm-blue focus:outline-none transition-all text-[16px] bg-white disabled:bg-gray-100 disabled:text-gray-400"
            >
              <option value="">Select role...</option>
              {ROLES.map((role) => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
          </div>

          {/* Scope type display */}
          {selectedRole && (
            <div className="bg-gray-50 rounded-xl px-4 py-3">
              <p className="text-sm text-storm-blue">
                Scope type: <span className="font-semibold text-deep-navy">{scopeType}</span>
              </p>
            </div>
          )}

          {/* Scope ID (unless org_admin which auto-selects) */}
          {selectedRole && scopeType !== 'organization' && (
            <div>
              <label className="block text-sm font-medium text-storm-blue mb-1">
                {scopeType === 'campus' ? 'Campus' : scopeType === 'ministry' ? 'Ministry' : 'Group'} *
              </label>
              <select
                value={selectedScopeId}
                onChange={(e) => setSelectedScopeId(e.target.value)}
                className="w-full px-3 py-2.5 min-h-touch rounded-xl border-2 border-gray-200 focus:border-storm-blue focus:outline-none transition-all text-[16px] bg-white"
              >
                <option value="">Select {scopeType}...</option>
                {scopeOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.name}</option>
                ))}
              </select>
              {scopeOptions.length === 0 && (
                <p className="text-xs text-sky-blue mt-1">
                  No {scopeType}s found in this organization
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="min-h-touch px-5 py-2.5 rounded-button font-semibold text-storm-blue bg-gray-100 hover:bg-gray-200 transition-all active:scale-[0.98]"
          >
            Cancel
          </button>
          <button
            onClick={handleGrant}
            disabled={!canGrant || loading}
            className={`min-h-touch px-5 py-2.5 rounded-button font-semibold text-white transition-all active:scale-[0.98] ${
              !canGrant || loading
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-gradient-button shadow-button'
            }`}
          >
            {loading ? 'Granting...' : 'Grant'}
          </button>
        </div>
      </div>
    </div>
  );
}
